import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    Stepper,
    Step,
    StepLabel,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    Divider,
    useTheme,
    alpha
} from '@mui/material';
import { usePermissions } from '../../../../utils/permissions';
import {
    Warning as WarningIcon,
    Error as ErrorIcon,
    CheckCircle as CheckCircleIcon,
    ReportProblem as ReportProblemIcon,
    Assignment as AssignmentIcon,
    Assessment as AssessmentIcon,
    Science as ScienceIcon,
    Delete as DeleteIcon,
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';

// Import CAPA Components
import IssueTrigger from './deviation-logs-components/IssueTrigger';
import DeviationReview from './deviation-logs-components/DeviationReview';
import RootCauseInvestigation from './deviation-logs-components/RootCauseInvestigation';

const steps = [
    {
        label: 'Report Issue',
        description: 'Flag production problems immediately',
        icon: <WarningIcon />,
        color: '#d32f2f',
        darkColor: '#b71c1c'
    },
    {
        label: 'QA Review',
        description: 'Quality team assesses severity',
        icon: <AssessmentIcon />,
        color: '#1976d2',
        darkColor: '#1565c0'
    },
    {
        label: 'Investigation',
        description: 'Find root cause of problem',
        icon: <ScienceIcon />,
        color: '#9333ea',
        darkColor: '#9333ea'
    }
];

export default function DeviationLog({ deviations, setDeviations, batchLogId, createBatchLog, showSnackbar, onDataUpdate, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const { canAccessPage, canEditPage, canDeletePage, canCloseDeviationIssues, canStartCAPAInvestigations, canCloseInvestigations } = usePermissions();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deviationToDelete, setDeviationToDelete] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const deviationLogRef = useRef(null);

    // Load deviations from database when component mounts or batchLogId changes
    const loadDeviations = async () => {
        if (!batchLogId) return;

        try {
            const { data, error } = await supabase
                .from('batch_log_deviations')
                .select('*')
                .eq('batch_log_id', batchLogId)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error loading deviations:', error);
                showSnackbar?.('Error loading deviations: ' + error.message, 'error');
                return;
            }

            setDeviations(data || []);
            onDataUpdate?.(); // Notify parent to refresh completion status
        } catch (error) {
            console.error('Error loading deviations:', error);
            showSnackbar?.('Error loading deviations: ' + error.message, 'error');
        }
    };

    // Load deviations on mount and when batchLogId changes
    useEffect(() => {
        loadDeviations();
    }, [batchLogId]);

    // Refresh deviations when switching between steps to ensure counts are current
    useEffect(() => {
        if (batchLogId) {
            loadDeviations();
        }
    }, [activeStep, batchLogId]);

    const handleDeleteDeviation = async (deviationId) => {
        try {
            // Delete from database
            const { error } = await supabase
                .from('batch_log_deviations')
                .delete()
                .eq('id', deviationId);

            if (error) {
                console.error('Error deleting deviation:', error);
                showSnackbar?.('Error deleting deviation: ' + error.message, 'error');
                return;
            }

            // Remove from local state
            setDeviations(prev => prev.filter(d => d.id !== deviationId));
            onDataUpdate?.(); // Notify parent to refresh completion status
            showSnackbar?.('Deviation deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting deviation:', error);
            showSnackbar?.('Error deleting deviation: ' + error.message, 'error');
        }
    };

    const confirmDelete = (deviation) => {
        setDeviationToDelete(deviation);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        if (deviationToDelete) {
            handleDeleteDeviation(deviationToDelete.id);
        }
        setDeleteConfirmOpen(false);
        setDeviationToDelete(null);
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'Critical': return '#f44336';
            case 'Major': return '#ff9800';
            case 'Minor': return '#ffeb3b';
            case 'Low': return '#4caf50';
            default: return '#9e9e9e';
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity) {
            case 'Critical': return <ErrorIcon />;
            case 'Major': return <WarningIcon />;
            case 'Minor': return <ReportProblemIcon />;
            case 'Low': return <CheckCircleIcon />;
            default: return <ReportProblemIcon />;
        }
    };

    const criticalCount = deviations.filter(d => 
        (d.severity_level === 'Critical' || d.issue_severity === 'critical') && 
        (d.status === 'open' || d.status === 'capa_required' || d.status === 'investigating')
    ).length;
    const capaCount = deviations.filter(d => 
        d.capa_required && 
        (d.status === 'capa_required' || d.status === 'investigating')
    ).length;

    const handleStepChange = (step) => {
        setActiveStep(step);
        // Auto-scroll to top of content
        if (deviationLogRef.current) {
            deviationLogRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const currentStepProgress = ((activeStep + 1) / steps.length) * 100;
    const currentStepColor = steps[activeStep]?.color || '#000000';
    const currentStepDarkColor = steps[activeStep]?.darkColor || '#333333';

    const getStepColors = (step) => {
        const colorSchemes = {
            0: { 
                main: '#d32f2f', 
                light: '#ffebee',
                border: '#f44336'
            },
            1: { 
                main: '#1976d2', 
                light: '#e3f2fd',
                border: '#2196f3'
            },
            2: { 
                main: '#9333ea', 
                light: '#f3e5f5',
                border: '#9333ea'
            }
        };
        return colorSchemes[step] || colorSchemes[0];
    };

    const canEdit = canEditPage('batch_logs_deviations');
    const canDelete = canDeletePage('batch_logs_deviations');
    const canCloseIssues = canCloseDeviationIssues();
    const canStartCAPA = canStartCAPAInvestigations();

    // Permission check for batch logs deviations
    if (!canAccessPage('batch_logs_deviations')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                px: 3
            }}>
                <Card sx={{
                    p: 8,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 600,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 6,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <ReportProblemIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        mb: 3, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2.5rem',
                        letterSpacing: '-0.8px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 4,
                        fontWeight: 500,
                        fontSize: '1.2rem'
                    }}>
                        Deviation Log Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        maxWidth: 450,
                        mx: 'auto'
                    }}>
                        You don't currently have the necessary permissions to access the Deviation Log. 
                        Contact your system administrator to request access or verify your role permissions.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box ref={deviationLogRef}>
                {/* Header */}
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ 
                                            p: 1.5, 
                                            borderRadius: 2, 
                                            background: `linear-gradient(${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})`,
                                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                            backdropFilter: 'blur(10px)',
                                            mr: 2
                                        }}>
                                            <ReportProblemIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                                Production Issue Tracker
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Report and resolve production problems
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Quick Stats */}
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <Card sx={{ 
                                            borderRadius: 4,
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            background: 'white',
                                            backdropFilter: 'blur(20px)',
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                            },
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}>
                                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                                    {deviations.filter(d => d.status === 'open' || d.status === 'capa_required' || d.status === 'investigating').length}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                    Active Issues
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <Card sx={{ 
                                            borderRadius: 4,
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            background: criticalCount > 0 ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.success.main, 0.05),
                                            backdropFilter: 'blur(20px)',
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                            },
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}>
                                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ 
                                                    fontWeight: 500, 
                                                    color: criticalCount > 0 ? theme.palette.error.main : theme.palette.success.main, 
                                                    mb: 0.5 
                                                }}>
                                                    {criticalCount}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                    Critical Issues
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <Card sx={{ 
                                            borderRadius: 4,
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            background: capaCount > 0 ? alpha(theme.palette.warning.main, 0.05) : alpha(theme.palette.grey[100], 0.5),
                                            backdropFilter: 'blur(20px)',
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                            },
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}>
                                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ 
                                                    fontWeight: 500, 
                                                    color: capaCount > 0 ? theme.palette.warning.main : theme.palette.text.disabled, 
                                                    mb: 0.5 
                                                }}>
                                                    {capaCount}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                    CAPA Required
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Progress Timeline */}
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Progress Timeline
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Step {activeStep + 1} of {steps.length}
                                    </Typography>
                                </Box>

                                <Stepper activeStep={activeStep} orientation="horizontal" alternativeLabel sx={{ mb: 3 }}>
                                    {steps.map((step, index) => (
                                        <Step key={step.label}>
                                            <StepLabel 
                                                onClick={() => handleStepChange(index)}
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    '& .MuiStepLabel-label': {
                                                        fontSize: '0.875rem',
                                                        fontWeight: index === activeStep ? 500 : 400,
                                                        color: index === activeStep ? step.color : theme.palette.text.secondary
                                                    },
                                                    '& .MuiStepIcon-root': {
                                                        color: index <= activeStep ? step.color : theme.palette.grey[300],
                                                        fontSize: '1.5rem'
                                                    }
                                                }}
                                            >
                                                {step.label}
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>

                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 2 }}>
                                        Progress
                                    </Typography>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={currentStepProgress}
                                        sx={{ 
                                            height: 8, 
                                            borderRadius: 4,
                                            backgroundColor: alpha(theme.palette.grey[300], 0.3),
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: currentStepColor,
                                                borderRadius: 4,
                                                background: `linear-gradient(90deg, ${currentStepColor}, ${currentStepDarkColor})`
                                            }
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Main Content Area */}
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${getStepColors(activeStep).border}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            overflow: 'hidden',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            {/* Step Header */}
                            <Box sx={{ 
                                p: 4, 
                                backgroundColor: alpha(getStepColors(activeStep).main, 0.05),
                                borderBottom: `1px solid ${alpha(getStepColors(activeStep).main, 0.2)}`
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ 
                                        width: 48,
                                        height: 48,
                                        borderRadius: 3,
                                        background: `linear-gradient(135deg, ${getStepColors(activeStep).main}, ${getStepColors(activeStep).main}dd)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        boxShadow: `0 4px 12px ${alpha(getStepColors(activeStep).main, 0.3)}`
                                    }}>
                                        {steps[activeStep]?.icon}
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                            {steps[activeStep]?.label}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                            {steps[activeStep]?.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Step Content */}
                            <Box sx={{ p: 4 }}>
                                {activeStep === 0 && (
                                    <IssueTrigger 
                                        batchLogId={batchLogId}
                                        createBatchLog={createBatchLog}
                                        showSnackbar={showSnackbar}
                                        canEdit={canEdit}
                                        batchTimeFormat={batchTimeFormat}
                                        onDeviationCreated={(newDeviation) => {
                                            loadDeviations(); // Refresh local deviations to get accurate data
                                            onDataUpdate?.(); // Notify parent to refresh completion status
                                            if (newDeviation.requires_capa || newDeviation.capa_required) {
                                                setActiveStep(2); // Jump to Investigation
                                                showSnackbar?.('CAPA issue reported! Moving to investigation...', 'info');
                                            } else {
                                                setActiveStep(1); // Go to QA Review
                                                showSnackbar?.('Issue reported! Moving to QA review...', 'success');
                                            }
                                        }}
                                    />
                                )}

                                {activeStep === 1 && (
                                    <DeviationReview 
                                        batchLogId={batchLogId}
                                        showSnackbar={showSnackbar}
                                        canCloseIssues={canCloseIssues}
                                        canStartCAPA={canStartCAPA}
                                        batchTimeFormat={batchTimeFormat}
                                        onCapaCreated={() => {
                                            loadDeviations(); // Refresh local deviations
                                            onDataUpdate?.(); // Notify parent to refresh completion status
                                            setActiveStep(2);
                                            showSnackbar?.('CAPA investigation started!', 'info');
                                        }}
                                        onIssueResolved={() => {
                                            loadDeviations(); // Refresh local deviations when issues are resolved
                                            onDataUpdate?.(); // Notify parent to refresh completion status
                                        }}
                                    />
                                )}

                                {activeStep === 2 && (
                                    <RootCauseInvestigation 
                                        batchLogId={batchLogId}
                                        showSnackbar={showSnackbar}
                                        canEdit={canEdit}
                                        canStartCAPA={canStartCAPA}
                                        canCloseInvestigations={canCloseInvestigations()}
                                        batchTimeFormat={batchTimeFormat}
                                        onInvestigationComplete={() => {
                                            loadDeviations(); // Refresh deviations to update CAPA count
                                            onDataUpdate?.(); // Notify parent to refresh completion status
                                            showSnackbar?.('Investigation completed successfully!', 'success');
                                        }}
                                        onInvestigationReopened={() => {
                                            loadDeviations(); // Refresh deviations when investigation is reopened
                                            onDataUpdate?.(); // Notify parent to refresh completion status
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Navigation */}
                            <Box sx={{
                                p: 3, 
                                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <Button 
                                    variant="outlined"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => {
                                        setActiveStep(Math.max(0, activeStep - 1));
                                        if (deviationLogRef.current) {
                                            deviationLogRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                    }}
                                    disabled={activeStep === 0}
                                    sx={{
                                        borderRadius: 3,
                                        px: 3,
                                        py: 1.5,
                                        borderColor: alpha(currentStepColor, 0.6),
                                        color: currentStepColor,
                                        fontWeight: 500,
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(10px)',
                                        '&:hover': {
                                            borderColor: currentStepColor,
                                            backgroundColor: alpha(currentStepColor, 0.04),
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px ${alpha(currentStepColor, 0.25)}`
                                        },
                                        '&:disabled': {
                                            borderColor: theme.palette.grey[300],
                                            color: theme.palette.text.disabled,
                                            background: alpha(theme.palette.grey[100], 0.5)
                                        },
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                >
                                    Previous
                                </Button>

                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {steps.map((_, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: index === activeStep ? currentStepColor : alpha(theme.palette.grey[400], 0.5),
                                                cursor: 'pointer',
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '&:hover': {
                                                    transform: 'scale(1.3)',
                                                    boxShadow: `0 2px 8px ${alpha(currentStepColor, 0.4)}`
                                                }
                                            }}
                                            onClick={() => {
                                                setActiveStep(index);
                                                if (deviationLogRef.current) {
                                                    deviationLogRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Button 
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => {
                                        setActiveStep(Math.min(steps.length - 1, activeStep + 1));
                                        if (deviationLogRef.current) {
                                            deviationLogRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                    }}
                                    disabled={activeStep === steps.length - 1}
                                    sx={{
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        backgroundColor: theme.palette.error.main,
                                        color: 'white',
                                        fontWeight: 500,
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                                        '&:hover': {
                                            backgroundColor: theme.palette.error.main,
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`
                                        },
                                        '&:disabled': {
                                            background: theme.palette.grey[400],
                                            color: theme.palette.grey[100],
                                            boxShadow: 'none'
                                        },
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                >
                                    Next
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteConfirmOpen} 
                    onClose={() => setDeleteConfirmOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                            maxHeight: '90vh',
                            overflow: 'hidden'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        fontWeight: 500,
                        fontSize: '1.2rem',
                        color: theme.palette.error.main,
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: alpha(theme.palette.error.main, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 3
                    }}>
                        Delete Issue Record
                    </DialogTitle>
                    <DialogContent sx={{ 
                        backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                        p: 3,
                        backdropFilter: 'blur(10px)',
                        mt: 3
                    }}>
                        <Typography variant="body1" sx={{ mb: 2, mt: 2, fontWeight: 500 }}>
                            Are you sure you want to delete this issue record? This action cannot be undone.
                        </Typography>
                        {deviationToDelete && (
                            <Box sx={{ 
                                p: 3, 
                                backgroundColor: alpha(theme.palette.grey[100], 0.8), 
                                borderRadius: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                mb: 3
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                    {deviationToDelete.deviation_type}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                                                {dayjs(deviationToDelete.occurred_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                            This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ 
                        p: 3, 
                        gap: 2, 
                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                    }}>
                        <Button 
                            onClick={() => setDeleteConfirmOpen(false)}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                background: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleConfirmDelete}
                            variant="contained"
                            startIcon={<DeleteIcon />}
                            disabled={!canDelete}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                backgroundColor: canDelete ? theme.palette.error.main : theme.palette.grey[400],
                                color: canDelete ? 'white' : theme.palette.grey[100],
                                fontWeight: 500,
                                boxShadow: canDelete ? `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}` : 'none',
                                '&:hover': {
                                    backgroundColor: canDelete ? theme.palette.error.main : theme.palette.grey[400],
                                    boxShadow: canDelete ? `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}` : 'none'
                                },
                                '&:disabled': {
                                    backgroundColor: theme.palette.grey[400],
                                    color: theme.palette.grey[100]
                                }
                            }}
                        >
                            {canDelete ? 'Delete Issue' : 'Read Only - No Delete Permissions'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
} 